import React from "react";
import { useTranslation } from "react-i18next";
import { MapPin, Phone, Clock, Eye, MessageCircle, Sparkles, Truck } from "lucide-react";

// Coords from the original Daita'n'Jack location (Viale Regina Margherita 88, Viareggio)
const MAP_URL = "https://www.google.com/maps/search/?api=1&query=43.86925989145173,10.241856653915669";
const PHONE = "+393407312505";

const HoursBlock = ({ range, slots }) => (
  <div className="border-2 border-zinc-800 hover:border-yellow-400/60 transition p-4 sm:p-5">
    <div className="font-heading uppercase text-yellow-400 text-sm sm:text-base tracking-widest mb-3 pb-3 border-b border-zinc-800">
      {range}
    </div>
    <ul className="space-y-1.5">
      {slots.map((slot, i) => (
        <li key={i} className="flex items-center gap-2 text-white font-mono text-sm sm:text-base tabular-nums">
          <span className="w-1.5 h-1.5 bg-yellow-400 flex-shrink-0" />
          <span>{slot}</span>
        </li>
      ))}
    </ul>
  </div>
);

export const Store = () => {
  const { t } = useTranslation();

  return (
    <section id="store" data-testid="store-section" className="relative py-24 overflow-hidden bg-zinc-950 border-t-2 border-zinc-900">
      <div className="absolute top-0 left-0 right-0 h-3 diagonal-stripes" />
      <div className="absolute inset-0 halftone-bg opacity-30" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
          <div>
            <div className="font-jp text-yellow-400 text-sm tracking-widest mb-3">{t("store.eyebrow")}</div>
            <h2 className="font-heading text-5xl sm:text-7xl uppercase text-white leading-none">
              {t("store.title_1")}<br />
              <span className="text-yellow-400">{t("store.title_accent")}</span>
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md text-lg">{t("store.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: Yellow card with address + CTAs */}
          <div className="lg:col-span-5 bg-yellow-400 text-black p-8 brutal-shadow-black relative overflow-hidden">
            <div className="absolute -top-6 -right-6 font-jp text-black/10 text-9xl select-none">店</div>

            <div className="relative space-y-6">
              <div className="inline-flex items-center gap-2 bg-black text-yellow-400 px-3 py-1.5">
                <Sparkles className="w-4 h-4" strokeWidth={3} />
                <span className="font-heading uppercase text-xs tracking-widest">Bottega Fisica</span>
              </div>

              <div className="space-y-5">
                <div>
                  <div className="font-heading uppercase text-xs tracking-widest opacity-70 mb-1 flex items-center gap-2">
                    <MapPin className="w-4 h-4" strokeWidth={3} /> {t("store.location_label")}
                  </div>
                  <div className="font-heading text-2xl uppercase leading-tight">{t("store.location_value")}</div>
                </div>

                <div>
                  <div className="font-heading uppercase text-xs tracking-widest opacity-70 mb-1 flex items-center gap-2">
                    <Phone className="w-4 h-4" strokeWidth={3} /> {t("store.phone_label")}
                  </div>
                  <a href={`tel:${PHONE}`} data-testid="store-phone" className="font-heading text-3xl uppercase leading-none hover:underline">
                    {t("store.phone_value")}
                  </a>
                </div>
              </div>

              <div className="bg-black/10 border-2 border-black/30 p-3 flex items-start gap-3">
                <Truck className="w-5 h-5 flex-shrink-0 mt-0.5" strokeWidth={3} />
                <div className="font-heading uppercase text-sm tracking-widest leading-tight">{t("store.pickup_note")}</div>
              </div>

              <div className="pt-2 border-t-2 border-black/20 grid grid-cols-2 gap-3">
                <a href={`tel:${PHONE}`} data-testid="store-cta-call"
                  className="bg-black text-yellow-400 font-heading uppercase tracking-widest px-3 py-3 hover:bg-zinc-800 transition flex items-center justify-center gap-2 text-sm">
                  <Phone className="w-4 h-4" strokeWidth={3} /> {t("store.cta_call")}
                </a>
                <a href={MAP_URL} target="_blank" rel="noreferrer" data-testid="store-cta-map"
                  className="border-4 border-black font-heading uppercase tracking-widest px-3 py-3 hover:bg-black hover:text-yellow-400 transition flex items-center justify-center gap-2 text-sm">
                  <MapPin className="w-4 h-4" strokeWidth={3} /> {t("store.cta_map")}
                </a>
              </div>
            </div>
          </div>

          {/* Right column: Hours + 3 features */}
          <div className="lg:col-span-7 space-y-6">
            {/* Hours card */}
            <div className="bg-zinc-900 border-2 border-zinc-800 p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-5">
                <Clock className="w-5 h-5 text-yellow-400" strokeWidth={3} />
                <h3 className="font-heading uppercase text-yellow-400 text-lg tracking-widest">{t("store.hours_label")}</h3>
              </div>
              <div data-testid="store-hours" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <HoursBlock
                  range={t("store.hours_range_weekday")}
                  slots={t("store.hours_lun_gio").split(" · ")}
                />
                <HoursBlock
                  range={t("store.hours_range_weekend")}
                  slots={t("store.hours_weekend").split(" · ")}
                />
              </div>
            </div>

            {/* 3 features compact */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { i: Eye, t: t("store.feat1_t"), d: t("store.feat1_d") },
                { i: MessageCircle, t: t("store.feat2_t"), d: t("store.feat2_d") },
                { i: Sparkles, t: t("store.feat3_t"), d: t("store.feat3_d") },
              ].map((f) => {
                const Icon = f.i;
                return (
                  <div key={f.t} className="bg-zinc-900 border-2 border-zinc-800 p-4 hover:border-yellow-400 transition group">
                    <div className="bg-yellow-400 text-black w-10 h-10 flex items-center justify-center mb-3 group-hover:scale-110 transition">
                      <Icon className="w-5 h-5" strokeWidth={3} />
                    </div>
                    <div className="font-heading text-base uppercase text-white mb-1 leading-tight">{f.t}</div>
                    <div className="text-xs text-zinc-400 leading-relaxed">{f.d}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between flex-wrap gap-3 border-t-2 border-zinc-800 pt-6">
          <div className="font-jp text-zinc-500 text-xs tracking-widest">
            ヴィアレッジョ · トスカーナ · イタリア
          </div>
          <div className="text-zinc-500 text-xs uppercase tracking-widest">Daita 'n' Jack — Store · Viale Regina Margherita 88</div>
        </div>
      </div>
    </section>
  );
};
